import { omitKeys, subsetTypeHasKey, toLocalDateString } from "@hex-insights/core";
import { FormState, formStateHasField } from "@hex-insights/forms";
import { CalendarEventFilterInput, mergeFilterInputs } from "../../../Utilities";
import { SharingPolicyUtilities } from "../../Utilities";
import * as CalendarEventFilterFormValues from "./form-values";

export function countActiveStandardFilters<
	K extends keyof CalendarEventFilterFormValues.FilterFormValues = keyof CalendarEventFilterFormValues.FilterFormValues,
>(formValues: Pick<CalendarEventFilterFormValues.FilterFormValues, K>) {
	let numActive = 0;
	if (subsetTypeHasKey(formValues, "status") && formValues.status !== null) {
		numActive++;
	}
	if (subsetTypeHasKey(formValues, "authorIDIn") && formValues.authorIDIn.length > 0) {
		numActive++;
	}
	if (subsetTypeHasKey(formValues, "sharedUserIDsIn") && formValues.sharedUserIDsIn.length > 0) {
		numActive++;
	}
	return numActive;
}

export function clearStandardFilters<
	K extends keyof CalendarEventFilterFormValues.FilterFormValues = keyof CalendarEventFilterFormValues.FilterFormValues,
>(formState: FormState<Pick<CalendarEventFilterFormValues.FilterFormValues, K>>) {
	if (formStateHasField(formState, "status")) {
		formState.formClearFunctions.status();
	}
	if (formStateHasField(formState, "authorIDIn")) {
		formState.formClearFunctions.authorIDIn();
	}
	if (formStateHasField(formState, "sharedUserIDsIn")) {
		formState.formClearFunctions.sharedUserIDsIn();
	}
}

export function resetStandardFilters<
	K extends keyof CalendarEventFilterFormValues.FilterFormValues = keyof CalendarEventFilterFormValues.FilterFormValues,
>(
	formState: FormState<Pick<CalendarEventFilterFormValues.FilterFormValues, K>>,
	initial: CalendarEventFilterFormValues.FilterFormValues,
) {
	if (formStateHasField(formState, "status")) {
		formState.formSetFunctions.status(initial.status);
	}
	if (formStateHasField(formState, "authorIDIn")) {
		formState.formSetFunctions.authorIDIn(initial.authorIDIn);
	}
	if (formStateHasField(formState, "sharedUserIDsIn")) {
		formState.formSetFunctions.sharedUserIDsIn(initial.sharedUserIDsIn);
	}
}

export function prepareFormValuesForConversion<
	K extends keyof CalendarEventFilterFormValues.FilterFormValues = keyof CalendarEventFilterFormValues.FilterFormValues,
>(formValues: Pick<CalendarEventFilterFormValues.FilterFormValues, K>) {
	return omitKeys(formValues, ["status", "sharedUserIDsIn"]) as Pick<
		CalendarEventFilterFormValues.FilterFormValues,
		Exclude<K, "status" | "sharedUserIDsIn">
	>;
}

export function toFilterInputs<
	K extends keyof CalendarEventFilterFormValues.FilterFormValues = keyof CalendarEventFilterFormValues.FilterFormValues,
>(
	formValues: Pick<CalendarEventFilterFormValues.FilterFormValues, K>,
	filterInput: CalendarEventFilterInput,
): CalendarEventFilterInput {
	const customFilterInputs: CalendarEventFilterInput[] = [];

	if (subsetTypeHasKey(formValues, "status") && formValues.status) {
		switch (formValues.status) {
			case "Upcoming":
				customFilterInputs.push(
					{ or: [{ startTimeGT: new Date().toISOString() }, { startTimeIsNull: true }] },
					{ or: [{ startDateGT: toLocalDateString(new Date()) }, { startDateIsNull: true }] },
				);
				break;
			case "Now":
				customFilterInputs.push(
					{
						or: [
							{
								startTimeLTE: new Date().toISOString(),
								endTimeGT: new Date().toISOString(),
							},
							{
								startTimeIsNull: true,
								endTimeIsNull: true,
							},
						],
					},
					{
						or: [
							{
								startDateLTE: toLocalDateString(new Date()),
								endDateGT: toLocalDateString(new Date()),
							},
							{
								startDateIsNull: true,
								endDateIsNull: true,
							},
						],
					},
				);
				break;
			case "Past":
				customFilterInputs.push(
					{
						or: [
							{
								endTimeLT: new Date().toISOString(),
							},
							{
								endTimeIsNull: true,
							},
						],
					},
					{
						or: [
							{
								endDateLT: toLocalDateString(new Date()),
							},
							{
								endDateIsNull: true,
							},
						],
					},
				);
				break;
		}
	}

	if (subsetTypeHasKey(formValues, "sharedUserIDsIn") && formValues.sharedUserIDsIn.length > 0) {
		customFilterInputs.push({
			sharingPolicy: [SharingPolicyUtilities.getUsersSharingPolicyFilter(formValues.sharedUserIDsIn)],
		});
	}

	return mergeFilterInputs(filterInput, ...customFilterInputs);
}
